import React, { forwardRef, useEffect, useMemo, useRef } from 'react';

import { Flex, Box } from '@chakra-ui/react';
import { Button, SankeyChart, Download } from '@mesh.ai/rabbit';
import { useQueryClient } from '@tanstack/react-query';
import { useSelectedView } from 'reducers/viewsSlice';

import NoDataImgV1 from 'assets/assetsV1/illustrations/newIllustrations/noDataForSmallView.svg';
import NoDataImg from 'assets/assetsV1/illustrations/sankeyChartNoReviewCycle.svg';

import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';
import { useExpandableContainer } from 'uiToolkitV1/ExpandableContainerProvider';
import { SelectDashed } from 'uiToolkitV1/Select';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';
import { TextBodyMedium, TextHeader3 } from 'uiToolkitV1/Typography';

import REACT_QUERY_KEYS_REVIEWS_INSIGHTS from '../TrendLineChart/constants/reactQueryKeysReviewsInsights';
import useSankeyChartData from './hooks/dataHooks';

export const PlotSankeyChart = forwardRef(({ chartData }, ref) => {
  const { isExpanded = false, onExpandClick } = useExpandableContainer();
  const { nodeData, nodes } = chartData;

  const groups = useMemo(() => {
    const fromNodeGroup = nodes?.find((node) => node.uuid === nodeData[0]?.fromNode);
    const toNodeGroup = nodes?.find((node) => node.uuid === nodeData[0]?.toNode);

    return [
      {
        id: 0,
        name: fromNodeGroup?.group,
      },
      {
        id: 1,
        name: toNodeGroup?.group,
      },
    ];
  }, [nodes, nodeData]);

  const mappedNodes = useMemo(
    () =>
      nodes.map((node) => ({
        id: node.uuid,
        name: node.name,
        group: groups.find((group) => group.name === node.group)?.id,
      })),
    [nodes, groups]
  );

  const links = useMemo(
    () =>
      nodeData.map((link) => ({
        source: link.fromNode,
        target: link.toNode,
        weight: link.weight,
      })),
    [nodeData]
  );

  return (
    <div className="rabbit-ds" style={{ position: 'relative', height: '100%' }}>
      {nodes?.length > 7 && !isExpanded && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backdropFilter: 'blur(5px)',
            zIndex: 2,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            WebkitBackdropFilter: 'blur(5px)',
          }}
        >
          <EmptyScreen
            isSmallTextSize
            smallTextMargin="4px"
            illustrationSrc={NoDataImgV1}
            heading="All the information for this graph cannot be shown in this view. Expand the graph to see it."
            // eslint-disable-next-line react/no-unstable-nested-components
            CTAComp={() => (
              <Button variant="outline" onClick={() => onExpandClick()}>
                Open expanded view
              </Button>
            )}
          />
        </div>
      )}

      <SankeyChart
        ref={ref}
        tooltipTitle="Average Progress"
        nodes={mappedNodes}
        groups={groups}
        links={links}
        weightLabel="People"
        style={{ position: 'relative', zIndex: 1 }}
      />
    </div>
  );
});

const ReviewsSankeyChart = ({
  pageSelectedReviewCycle,
  selectedReviewCycleForSankeyChart,
  setSelectedReviewCycleForSankeyChart,
  reviewCycleListForSankeyChart,
}) => {
  const chartRef = useRef(null);
  const { isExpanded = false } = useExpandableContainer();
  const queryClient = useQueryClient();
  const selectedCohort = useSelectedView();

  const requests = useMemo(() => {
    if (!selectedReviewCycleForSankeyChart || selectedReviewCycleForSankeyChart === pageSelectedReviewCycle) {
      return {
        request1: {
          cohortId: selectedCohort?.id,
          groupByKeys: {
            uuid: null,
            keyName: 'employeeId',
            displayName: 'Individual',
            enabled: true,
          },
          source: {
            uuid: pageSelectedReviewCycle?.uuid,
            keyName: pageSelectedReviewCycle?.keyName,
            enabled: true,
            reviewType: pageSelectedReviewCycle?.reviewType,
            displayName: pageSelectedReviewCycle?.displayName,
            performanceEvaluationGrid: pageSelectedReviewCycle?.performanceEvaluationGrid,
          },
        },
        request2: null,
      };
    }
    return {
      request1: {
        cohortId: selectedCohort?.id,
        groupByKeys: {
          uuid: null,
          keyName: 'employeeId',
          displayName: 'Individual',
          enabled: true,
        },
        source: {
          uuid: selectedReviewCycleForSankeyChart?.uuid,
          keyName: selectedReviewCycleForSankeyChart?.keyName,
          enabled: true,
          reviewType: selectedReviewCycleForSankeyChart?.reviewType,
          displayName: selectedReviewCycleForSankeyChart?.displayName,
          performanceEvaluationGrid: selectedReviewCycleForSankeyChart?.performanceEvaluationGrid,
        },
      },
      request2: {
        cohortId: selectedCohort?.id,
        groupByKeys: {
          uuid: null,
          keyName: 'employeeId',
          displayName: 'Individual',
          enabled: true,
        },
        source: {
          uuid: pageSelectedReviewCycle?.uuid,
          keyName: pageSelectedReviewCycle?.keyName,
          enabled: true,
          reviewType: pageSelectedReviewCycle?.reviewType,
          displayName: pageSelectedReviewCycle?.displayName,
          performanceEvaluationGrid: pageSelectedReviewCycle?.performanceEvaluationGrid,
        },
      },
    };
  }, [selectedReviewCycleForSankeyChart, pageSelectedReviewCycle, selectedCohort]);

  const { data, isLoading } = useSankeyChartData(requests);

  const handleChange = (value) => {
    setSelectedReviewCycleForSankeyChart(value);
    queryClient.invalidateQueries([REACT_QUERY_KEYS_REVIEWS_INSIGHTS.reviewsSankeyChart]);
  };

  useEffect(() => {
    if (data?.srcReviewCycle) {
      const preSelectedReviewCycleForSankeyChart = reviewCycleListForSankeyChart?.filter(
        (value) => value.displayName === data?.srcReviewCycle?.name
      );
      setSelectedReviewCycleForSankeyChart(preSelectedReviewCycleForSankeyChart[0]);
    }
  }, [data, selectedReviewCycleForSankeyChart, pageSelectedReviewCycle]);

  const onDownloadClick = () => {
    chartRef?.current?.download();
  };

  if (isLoading) {
    return (
      <Box mt="48px">
        <SkeletonLoader />
      </Box>
    );
  }

  return (
    <Flex h="100%" flexDirection="column">
      <Flex mr="40px">
        <TextHeader3 ml="6px" pt="6px" bold color="text.g1">
          Performance distribution
        </TextHeader3>
        <Flex ml="auto">
          <TextBodyMedium m="10px 4px 0px 0px">Compare to:</TextBodyMedium>
          <Flex
            zIndex={99}
            sx={{
              '.select': {
                '.select__value-container': {
                  marginBottom: '-4px',
                },
                '.select__single-value': {
                  whiteSpace: 'nowrap',
                  maxWidth: '132px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  textAlign: 'center',
                },
                '.select__menu-list': {
                  height: '250px',
                },
              },
            }}
          >
            <SelectDashed
              placeholder="Select review cycle"
              boldText
              isSearchable
              value={selectedReviewCycleForSankeyChart}
              options={reviewCycleListForSankeyChart}
              getOptionValue={(option) => option.name}
              getOptionLabel={(option) => option.displayName}
              handleChange={handleChange}
              withBackground={false}
            />
          </Flex>
        </Flex>
        {/* show download button when node length is less than 7 in non expanded view */}
        {data?.nodes?.length > 7 && !isExpanded ? null : (
          <Button variant="text" size="icon" iconRight={Download} onClick={onDownloadClick} />
        )}
      </Flex>
      {data?.nodes && data.nodeData && data?.nodes.length ? (
        <Box mt="10px" minH={0} h="full" minW={0} w="full" flex={1}>
          {data?.nodes && data?.nodeData && <PlotSankeyChart chartData={data} ref={chartRef} isExpanded={isExpanded} />}
        </Box>
      ) : (
        <EmptyScreen
          isSmallImg={!isExpanded}
          isSmallTextSize={!isExpanded}
          illustrationSrc={NoDataImg}
          heading="Change Review Cycle"
          description="There is no data for the selected review cycle. Please select another review cycle"
        />
      )}
    </Flex>
  );
};

export default ReviewsSankeyChart;
